import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import DebtKanbanApi from 'dashboard/api/debtKanban';

export const PERIOD_FILTER_OPTIONS = [7, 30, 90, 365];
// 90 dias, e não 30, porque é a janela em que os cards EXISTEM: o semeador
// só cria card para conversa ativa nos últimos 90 dias
// (Debt::KanbanStageSeeder::ACTIVE_WINDOW, lib/debt/kanban_stage_seeder.rb).
// Com 30, o board abria mostrando uma fração do funil que o próprio produto
// materializou — medido em produção: 192/252/215/33/5 na tela contra
// 584/875/490/78/21 semeados, ou seja ~40% do total, sem nada na tela
// explicando o recorte. O padrão tem que ser a janela do dado, não um número
// menor escolhido por precaução; quem quiser encurtar tem a pastilha de
// período, que continua sempre visível e é o que sustenta a garantia do PRD
// ("filtro que a pessoa não vê é mentira sobre o que ela está olhando").
export const DEFAULT_PERIOD_DAYS = 90;
const SEARCH_DEBOUNCE_MS = 300;

const daysAgoIso = days =>
  new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

const emptyFilters = () => ({
  assignees: [],
  inboxes: [],
  debtStates: [],
  labels: [],
  priorities: [],
  statuses: [],
});

/**
 * Centraliza o estado da página do board (T3.4): funis, filtros, colunas
 * paginadas e o movimento de card entre colunas (drag and drop). Usa a MESMA
 * API que o widget do painel (T3.3) — board(), boardColumn(), moveCard(),
 * card() — sem tocar os controllers.
 *
 * Otimismo com reversão real (mesma garantia do useDebtKanbanWidget, PRD T3.3
 * secao 3): falha de rede desfaz a mutação local; 409 recarrega o card
 * autoritativo em vez de reaplicar a intenção do arrasto por cima do estado
 * de outra pessoa (PRD T3.4 secao 4, ponto 5).
 *
 * @returns {Object} estado e ações para a página do board.
 */
export function useKanbanBoard() {
  const { t } = useI18n();

  const funnels = ref([]);
  const funnelsLoaded = ref(false);
  const funnelsError = ref(false);
  const selectedFunnelId = ref(null);

  const selectedFunnel = computed(
    () =>
      funnels.value.find(funnel => funnel.id === selectedFunnelId.value) || null
  );

  const stages = computed(() => {
    const funnel = selectedFunnel.value;
    if (!funnel) return [];
    return [...funnel.stages].sort((a, b) => a.position - b.position);
  });

  const stageById = computed(() => {
    const map = new Map();
    stages.value.forEach(stage => map.set(stage.id, stage));
    return map;
  });

  const periodDays = ref(DEFAULT_PERIOD_DAYS);
  const filters = reactive(emptyFilters());
  const searchQuery = ref('');
  let searchDebounceTimer = null;

  // columnsByStageId é um objeto reativo simples (não Map): Vue 3 observa
  // Map, mas manter objeto simplifica a leitura no template (v-for +
  // acesso direto) e o board nunca tem tantas colunas a ponto de a
  // diferença de performance importar.
  const columnsByStageId = reactive({});
  const isBoardLoading = ref(false);
  const hasLoadedBoardOnce = ref(false);
  const boardError = ref(false);

  let boardRequestToken = 0;

  // A5: boardRequestToken protege refetch contra refetch, e não protegia
  // nada entre um refetch e um PATCH pendente. Um retrato do servidor tirado
  // ANTES de um movimento aceito chegava depois dele e desfazia visualmente
  // um movimento que o servidor já tinha gravado (medido: PATCH 200 com o
  // card em B, board mostrando A). Janela real: a busca com debounce de
  // 300ms, ou trocar um filtro durante o arrasto.
  //
  // cardMutationTicket anda em toda BORDA de mutação (início e fim), então
  // "o ticket mudou" cobre os três casos: mutação iniciada durante o fetch,
  // terminada durante o fetch, e iniciada antes e ainda em voo. Nesses
  // casos o retrato é descartado e um novo é pedido quando não houver mais
  // mutação em voo — publicar o antigo é publicar um estado que já se sabe
  // errado.
  let cardMutationTicket = 0;
  let cardMutationsInFlight = 0;
  let refetchQueuedAfterMutations = false;

  const isBoardSnapshotStale = ticketAtStart =>
    cardMutationTicket !== ticketAtStart || cardMutationsInFlight > 0;

  const hasActiveFilters = computed(() => {
    return (
      filters.assignees.length > 0 ||
      filters.inboxes.length > 0 ||
      filters.debtStates.length > 0 ||
      filters.labels.length > 0 ||
      filters.priorities.length > 0 ||
      filters.statuses.length > 0 ||
      searchQuery.value !== ''
    );
  });

  const hasAnyCards = computed(() =>
    Object.values(columnsByStageId).some(column => column.cards.length > 0)
  );

  const isBoardEmptyByFilters = computed(
    () =>
      hasLoadedBoardOnce.value &&
      !isBoardLoading.value &&
      !boardError.value &&
      stages.value.length > 0 &&
      !hasAnyCards.value
  );

  const queryParams = computed(() => {
    if (!selectedFunnelId.value) return null;

    const params = {
      funnel_id: selectedFunnelId.value,
      last_activity_after: daysAgoIso(periodDays.value),
    };
    if (filters.assignees.length) {
      params.assignee_id = filters.assignees.map(item => item.id);
    }
    if (filters.inboxes.length) {
      params.inbox_id = filters.inboxes.map(item => item.id);
    }
    if (filters.debtStates.length) {
      params.debt_state = filters.debtStates.map(item => item.id);
    }
    if (filters.labels.length) {
      params.label = filters.labels.map(item => item.id);
    }
    if (filters.priorities.length) {
      params.priority = filters.priorities.map(item => item.id);
    }
    if (filters.statuses.length) {
      params.status = filters.statuses.map(item => item.id);
    }
    if (searchQuery.value) {
      params.q = searchQuery.value;
    }
    return params;
  });

  // `cards`, `count`, `countCapped` e `nextCursor` de uma coluna são UM
  // estado só. Todas as mutações passam pelas funções desta seção — mexer em
  // `cards` por fora (o contrato antigo: "quem chama move o card entre as
  // listas e o composable ajusta o derivado") deixava cursor e contagem para
  // trás, e era a raiz comum dos achados A1, A2, A6 e A7 do refutador.
  //
  // hasMore vem do servidor (next_cursor não nulo), não de comparar o
  // tamanho da página com um espelho local de PER_COLUMN_DEFAULT: o espelho
  // divergia em silêncio se per_column mudasse, e obrigava a confiar num
  // número que capped_count trunca.
  const buildColumnState = column => ({
    stageId: column.stage_id,
    count: column.count,
    countCapped: column.count_capped,
    countCap: column.count_cap,
    cards: column.cards,
    nextCursor: column.next_cursor,
    hasMore: Boolean(column.next_cursor),
    isLoadingMore: false,
    loadMoreError: false,
  });

  // A6: o servidor nunca reporta mais que count_cap — acima disso ele diz
  // "500+" (count_capped) e para de contar. Sem este teto, mover um card
  // para uma coluna já no teto publicava "501+ conversations", um número que
  // o servidor jamais enviou. Coluna já truncada não tem número exato para
  // somar nem subtrair: "500+" continua verdadeiro com um card a mais ou a
  // menos, e o valor volta a ser exato no próximo refetch.
  const setColumnCount = (column, value) => {
    if (column.countCapped) return;

    const next = Math.max(0, value);
    if (column.countCap != null && next > column.countCap) {
      column.count = column.countCap;
      column.countCapped = true;
      return;
    }
    column.count = next;
  };

  // Mantém a ordem entered_stage_at asc da coluna (mesma ordem que o backend
  // devolve): um push cego ao fim quebraria essa ordem até o próximo
  // refetch, para qualquer coluna que já tenha mais de um card.
  const insertCardByEnteredAt = (cards, card) => {
    const index = cards.findIndex(
      existing =>
        new Date(existing.entered_stage_at) > new Date(card.entered_stage_at)
    );
    if (index === -1) {
      cards.push(card);
    } else {
      cards.splice(index, 0, card);
    }
  };

  const detachCardEverywhere = cardId => {
    Object.values(columnsByStageId).forEach(column => {
      const index = column.cards.findIndex(card => card.id === cardId);
      if (index !== -1) column.cards.splice(index, 1);
    });
  };

  /**
   * ÚNICA função que muda a coluna de um card. Os três caminhos de movimento
   * (arrasto via notifyColumnChange, teclado via moveCardToStage na página,
   * reconciliação de 409) passam por aqui, então nenhum deles pode esquecer
   * metade do estado.
   *
   * É idempotente quanto à presença física: o vuedraggable já fez o splice
   * na lista de destino quando o arrasto chega aqui, e a reinserção corrige
   * a posição (o drop pode cair em qualquer índice) sem duplicar o card.
   *
   * @param {Object} params
   * @param {Object} params.card card a mover.
   * @param {number|null} params.fromStageId etapa de onde ele sai.
   * @param {number|null} params.toStageId etapa para onde ele vai.
   */
  const relocateCard = ({ card, fromStageId, toStageId }) => {
    detachCardEverywhere(card.id);

    const fromColumn = columnsByStageId[fromStageId];
    if (fromColumn) setColumnCount(fromColumn, fromColumn.count - 1);

    const toColumn = columnsByStageId[toStageId];
    if (!toColumn) return;

    insertCardByEnteredAt(toColumn.cards, card);
    setColumnCount(toColumn, toColumn.count + 1);
  };

  const cardIdsOnBoard = () =>
    new Set(
      Object.values(columnsByStageId).flatMap(column =>
        column.cards.map(card => card.id)
      )
    );

  // Página nova da mesma coluna. Descarta id que o board JÁ TEM, em qualquer
  // coluna — não só nesta. Dois casos reais, os dois com a mesma origem (a
  // página foi montada pelo servidor antes de um movimento local):
  //   - o card foi movido PARA cá depois da fronteira do cursor: o servidor
  //     legitimamente o devolve na página seguinte, e aceitá-lo duplicaria a
  //     chave do item-key do vuedraggable (A2);
  //   - o card foi movido DAQUI para outra coluna enquanto a página vinha: a
  //     página ainda o traz, e aceitá-lo o mostraria em duas colunas ao mesmo
  //     tempo. Descartar deixa o board momentaneamente atrasado sobre ESSE
  //     card (ele fica onde o movimento local o pôs) em vez de duplicado — o
  //     próximo refetch resolve, e um id duplicado, não.
  // Insere por entered_stage_at, não push: um card movido para cá é o mais
  // recente da etapa e tem que continuar depois dos cards antigos que chegam
  // agora.
  const appendPageToColumn = (column, page) => {
    const known = cardIdsOnBoard();
    page.cards.forEach(card => {
      if (known.has(card.id)) return;
      insertCardByEnteredAt(column.cards, card);
    });
    column.countCapped = page.count_capped;
    column.countCap = page.count_cap;
    column.count = page.count;
    column.nextCursor = page.next_cursor;
    column.hasMore = Boolean(page.next_cursor);
  };

  const fetchFunnels = async () => {
    funnelsError.value = false;
    try {
      const { data } = await DebtKanbanApi.funnels();
      funnels.value = data;
      funnelsLoaded.value = true;
      // A seleção é revalidada contra a lista NOVA, e não só preenchida
      // quando está vazia: a administração de funis (T3.5) pode arquivar ou
      // remover justamente o funil selecionado, e manter o id de um funil que
      // não está mais na lista deixava o board preso em zero coluna, sem
      // nenhum estado que explicasse a tela vazia.
      const stillListed = data.some(
        funnel => funnel.id === selectedFunnelId.value
      );
      if (!stillListed) {
        selectedFunnelId.value = data.length ? data[0].id : null;
      }
    } catch (error) {
      funnelsError.value = true;
    }
  };

  const retryFunnels = () => fetchFunnels();

  const fetchBoard = async () => {
    const params = queryParams.value;
    if (!params) return;

    boardRequestToken += 1;
    const requestToken = boardRequestToken;
    const mutationTicketAtStart = cardMutationTicket;
    isBoardLoading.value = true;
    boardError.value = false;

    try {
      const { data } = await DebtKanbanApi.board(params);
      if (requestToken !== boardRequestToken) return;
      // Retrato obsoleto em relação às mutações locais: descartado, e
      // repetido — agora, se nada mais está em voo; quando a última mutação
      // terminar (endCardMutation), se ainda há PATCH pendente.
      if (isBoardSnapshotStale(mutationTicketAtStart)) {
        refetchQueuedAfterMutations = cardMutationsInFlight > 0;
        if (!refetchQueuedAfterMutations) fetchBoard();
        return;
      }

      Object.keys(columnsByStageId).forEach(key => {
        delete columnsByStageId[key];
      });
      data.columns.forEach(column => {
        columnsByStageId[column.stage_id] = buildColumnState(column);
      });
    } catch (error) {
      if (requestToken !== boardRequestToken) return;
      boardError.value = true;
    } finally {
      if (requestToken === boardRequestToken) {
        isBoardLoading.value = false;
        hasLoadedBoardOnce.value = true;
      }
    }
  };

  const retry = () => fetchBoard();

  // Recarga depois de configurar funis e etapas (T3.5). O fetchBoard explícito
  // é necessário: o watch abaixo observa `queryParams` (funil e filtros), e
  // criar, renomear, reordenar ou remover uma ETAPA não muda nenhum deles —
  // sem esta chamada, a coluna nova só apareceria no próximo recarregamento da
  // página.
  const reloadAfterFunnelAdmin = async () => {
    await fetchFunnels();
    await fetchBoard();
  };

  const beginCardMutation = () => {
    cardMutationTicket += 1;
    cardMutationsInFlight += 1;
  };

  const endCardMutation = () => {
    cardMutationTicket += 1;
    cardMutationsInFlight = Math.max(0, cardMutationsInFlight - 1);
    if (cardMutationsInFlight > 0 || !refetchQueuedAfterMutations) return;

    refetchQueuedAfterMutations = false;
    fetchBoard();
  };

  watch(
    () => JSON.stringify(queryParams.value),
    () => fetchBoard()
  );

  const selectFunnel = funnelId => {
    selectedFunnelId.value = funnelId;
  };

  const setPeriodDays = days => {
    periodDays.value = days;
  };

  const setFilter = (key, value) => {
    filters[key] = value;
  };

  const setSearchQuery = value => {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => {
      searchQuery.value = value;
    }, SEARCH_DEBOUNCE_MS);
  };

  const clearFilters = () => {
    Object.assign(filters, emptyFilters());
    clearTimeout(searchDebounceTimer);
    searchQuery.value = '';
  };

  const loadMoreColumn = async stageId => {
    const column = columnsByStageId[stageId];
    const params = queryParams.value;
    if (!column || !params || column.isLoadingMore || !column.hasMore) return;

    column.isLoadingMore = true;
    column.loadMoreError = false;

    try {
      const { data } = await DebtKanbanApi.boardColumn({
        ...params,
        stageId,
        cursor: column.nextCursor,
      });
      appendPageToColumn(column, data);
    } catch (error) {
      column.loadMoreError = true;
    } finally {
      column.isLoadingMore = false;
    }
  };

  const revertCardMove = ({ card, targetStageId, originStageId, previous }) => {
    Object.assign(card, previous);
    relocateCard({
      card,
      fromStageId: targetStageId,
      toStageId: originStageId,
    });
  };

  // 409: a intenção do arrasto NUNCA é reaplicada. O card sai da coluna onde
  // o drag o largou e vai para onde o servidor diz que ele está agora — que
  // pode ser uma terceira coluna (quem moveu antes escolheu outro destino),
  // não a origem nem o alvo do arrasto (PRD T3.4 secao 4, ponto 5).
  //
  // A7: a reconciliação usa relocateCard como todo o resto. Antes ela era o
  // único caminho que empurrava o card com push ao fim da coluna de destino,
  // quebrando a ordem entered_stage_at asc que o próprio arquivo declara — o
  // teste de 409 não pegava porque a coluna de destino do fixture estava
  // vazia, onde push e inserção ordenada são indistinguíveis.
  const reconcileConflict = async (card, targetStageId) => {
    try {
      const { data } = await DebtKanbanApi.card(card.conversation.id);
      const authoritative = data.card;
      if (!authoritative) {
        relocateCard({ card, fromStageId: targetStageId, toStageId: null });
        return;
      }

      card.stage_id = authoritative.stage.id;
      card.entered_stage_at = authoritative.entered_stage_at;
      card.lock_version = authoritative.lock_version;

      // Etapa de destino fora do board visível (outro funil, ou etapa cuja
      // página desta coluna ainda não foi carregada): relocateCard tira o
      // card do board em vez de mostrar um estado inconsistente — limite
      // aceito e registrado nos RISCOS do retorno desta unidade.
      relocateCard({
        card,
        fromStageId: targetStageId,
        toStageId: authoritative.stage.id,
      });
    } catch (error) {
      // Best-effort: sem o card autoritativo, ele fica fora de ambas as
      // colunas em vez de arriscar mostrar um estado que já sabemos estar
      // errado (ele não está mais na coluna do arrasto, e não sabemos onde
      // está).
      relocateCard({ card, fromStageId: targetStageId, toStageId: null });
    }
  };

  // 403 (role_forbidden) e 422 (wip_limit_exceeded) chegam com uma mensagem
  // específica do backend (base_controller.rb#render_role_forbidden e
  // #render_wip_limit_exceeded) que caía no MOVE_ERROR genérico e era
  // descartada — a pessoa via "não foi possível mover" sem o motivo, que é
  // justamente a parte acionável. O genérico continua valendo para falha de
  // rede e para erro sem corpo conhecido.
  const moveErrorMessage = error =>
    error?.response?.data?.error || t('KANBAN.BOARD.CARD.MOVE_ERROR');

  const moveCard = async ({ card, targetStageId, originStageId }) => {
    if (targetStageId === originStageId) return;

    const targetColumn = columnsByStageId[targetStageId];
    const originColumn = columnsByStageId[originStageId];
    if (!targetColumn || !originColumn) return;

    const previous = {
      stage_id: card.stage_id,
      entered_stage_at: card.entered_stage_at,
      lock_version: card.lock_version,
    };

    // Nunca reaproveitar entered_stage_at da etapa antiga (lição já paga no
    // widget T3.3: SLA da etapa nova combinado com timestamp da etapa
    // anterior mentia o alarme). O timestamp precisa estar novo ANTES de
    // relocateCard, que posiciona o card por ele.
    card.stage_id = targetStageId;
    card.entered_stage_at = new Date().toISOString();
    // Quem chama pode já ter movido o card fisicamente (o @change do
    // vuedraggable faz o splice antes de nos avisar) ou não (o caminho por
    // teclado). relocateCard trata os dois: é ele, e só ele, que deixa
    // lista, contagem e cursor da coluna coerentes entre si.
    relocateCard({
      card,
      fromStageId: originStageId,
      toStageId: targetStageId,
    });

    beginCardMutation();
    try {
      // cards#update devolve o card ACHATADO via a mesma partial _card usada
      // por board/index e board/column (cards/update.json.jbuilder), sem
      // envelope — ao contrário de conversations/:id/card (endpoint
      // consumido por DebtKanbanApi.card, usado abaixo em
      // reconcileConflict), que envelopa em `card` com uma forma própria.
      // `data` já É o card; principalmente lock_version precisa vir daqui,
      // senão o próximo PATCH deste card envia um lock_version obsoleto e
      // toda segunda movimentação vira 409.
      const { data } = await DebtKanbanApi.moveCard(card.id, {
        stageId: targetStageId,
        lockVersion: previous.lock_version,
      });
      Object.assign(card, data);
    } catch (error) {
      if (error?.response?.status === 409) {
        await reconcileConflict(card, targetStageId);
        useAlert(t('KANBAN.BOARD.CARD.MOVE_CONFLICT'));
      } else {
        revertCardMove({ card, targetStageId, originStageId, previous });
        useAlert(moveErrorMessage(error));
      }
    } finally {
      endCardMutation();
    }
  };

  // Coordena os eventos @change dos <draggable> das colunas (vuedraggable
  // dispara `removed` na lista de origem e `added` na de destino,
  // sincronamente, para o mesmo arrasto). `sort: false` no draggable já
  // impede o reordenamento dentro da própria coluna (PRD T3.4 secao 4:
  // "arrastar dentro da coluna não reordena") — o guard de `moved` aqui é
  // defesa em profundidade, não o controle principal.
  const dragOriginByCardId = new Map();

  const notifyColumnChange = (stageId, evt) => {
    if (evt.removed) {
      dragOriginByCardId.set(evt.removed.element.id, stageId);
    }
    if (evt.added) {
      const originStageId = dragOriginByCardId.get(evt.added.element.id);
      dragOriginByCardId.delete(evt.added.element.id);
      if (originStageId != null && originStageId !== stageId) {
        moveCard({
          card: evt.added.element,
          targetStageId: stageId,
          originStageId,
        });
      }
    }
  };

  fetchFunnels();

  return {
    funnels,
    funnelsLoaded,
    funnelsError,
    selectedFunnelId,
    selectedFunnel,
    stages,
    stageById,
    periodDays,
    filters,
    searchQuery,
    hasActiveFilters,
    columnsByStageId,
    isBoardLoading,
    hasLoadedBoardOnce,
    boardError,
    isBoardEmptyByFilters,
    hasAnyCards,
    selectFunnel,
    setPeriodDays,
    setFilter,
    setSearchQuery,
    clearFilters,
    loadMoreColumn,
    moveCard,
    notifyColumnChange,
    retry,
    retryFunnels,
    reloadAfterFunnelAdmin,
  };
}
